import { faUser } from '@fortawesome/pro-light-svg-icons'
import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { CustomerProjectTypeModelType } from '@teinor/erp/types/company/customer/customerProjectType'
import { useCallback, useEffect, useState } from 'react'
import { WhiteBox } from '../../../../../../baseComponents/AppLayout/WhiteBox'
import { ButtonSecondary } from '../../../../../../baseComponents/Button'
import { BigTitleAndRightLayout } from '../../../../../../baseComponents/Layout/TitleAndRightLayout'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import TwinTableCard, { RenderTwinTableCards } from '../../../../../../baseComponents/TwinTable/TwinTableCard'
import { SearchBarTable } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents'
import { stringMatchWithSearch } from '../../../../../../utils/globals/search'
import useEditDeleteModal from '../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { getAllCustomerProjectTypes, getSingleEmployeeParam } from '../../../../../../utils/reducers/getters'
import ModalFavouriteCustomerProjectFlow from './ModalFavouriteCustomerProjectFlow'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import TwinTrans from '../../../../../../baseComponents/TwinTrans'
import locations from '../../../../../../utils/globals/locations'
import { projectTypes, ProjectTypesInversedKeys, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import { CustomerProjectTypePayload } from '../../../../../../utils/reducers/customers/customerProjectTypes'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import withLoading from '../../../../../../utils/hoc/withLoading'

interface CustomerProjectTypeModelTypeExt extends CustomerProjectTypeModelType {
    checked?: boolean
    order: number
}
type RenderByFavourites = {
    myFavouritesBoards: JSX.Element[]
    othersBoards: JSX.Element[]
}
interface CustomerProjectStatusesFlowProps extends CustomerProjectTypesStatusesFlowLogicProps { }

const CustomerProjectTypesStatusesFlow: React.FC<CustomerProjectStatusesFlowProps> = ({ projectTypeStatus = 1 }) => {
    const { t } = useTwinTranslation()
    const { data, generalSearch, setGeneralSearch, openModal, setOpenModal, getData } = useCustomerProjectTypesStatusesFlowLogic({ projectTypeStatus })
    const myBasePathDict: Record<ProjectTypesKeys, string>= {
        0: locations.customerOppStatusFlow,
        1: locations.customerProjectsStatusFlow
    }
    const basePath = myBasePathDict[projectTypeStatus]

    const myBasePathToTableDict: Record<ProjectTypesKeys, string>= {
        0: locations.customerOpportunities,
        1: locations.customerProjects
    }
    const basePathToTable = myBasePathToTableDict[projectTypeStatus]
    const renderThisJSON: RenderByFavourites = {
        myFavouritesBoards: [],
        othersBoards: []
    }
    for (const element of data) {
        const includesSearch = stringMatchWithSearch(generalSearch, element?.name)
        if (includesSearch) {
            const elementJSX = <TwinTableCard key={element.id} id={String(element.id)} title={element?.name || ''} img={element?.photo || '/no-image-small.png'} icon={faUser} lastPath='main' {...{ basePath }} />
            if (element.checked) {
                renderThisJSON.myFavouritesBoards.push(elementJSX)
            } else {
                renderThisJSON.othersBoards.push(elementJSX)
            }
        }
    }

    return (
        <BigTitleAndRightLayout title={t('boards', 'Tableros')} RightHeader={
            <Link to={basePathToTable}>
                <ButtonSecondary>
                    <TwinTrans transKey='changeView'>Cambiar vista</TwinTrans>
                </ButtonSecondary>
            </Link>
        }>
            <WhiteBox className='flex flex-col flex-auto px-40 py-50'>
                <div className='flex flex-col flex-auto'>
                    <div className='flex justify-between items-center flex-wrap gap-15 mb-20'>
                        <SearchBarTable onChange={setGeneralSearch} placeholder={t('searchDots', 'Buscar...')} />
                        <ButtonSecondary onClick={() => setOpenModal({ type: 'edit', allRowData: {} })}><TwinIcon icon={faPlus} className='mr-8'></TwinIcon><TwinTrans transKey={'configureFavorites'}>Configurar favoritos</TwinTrans></ButtonSecondary>
                    </div>
                    <div className='flex-auto h-1 overflow-auto'>
                        <RenderTwinTableCards title={t('myFavouriteBoards', 'Mis tableros favoritos')} twinTableCards={renderThisJSON.myFavouritesBoards} />
                        <RenderTwinTableCards title={t('otherBoards', 'Otros tableros')} twinTableCards={renderThisJSON.othersBoards} className='mt-20' />
                    </div>
                </div>
            </WhiteBox>
            {openModal?.type === 'edit' ? <ModalFavouriteCustomerProjectFlow setOpened={() => setOpenModal(null)} onSubmit={getData} customerProjectTypes={data} /> : null}
        </BigTitleAndRightLayout>
    )
}

interface CustomerProjectTypesStatusesFlowLogicProps {
    projectTypeStatus: ProjectTypesKeys
}

const useCustomerProjectTypesStatusesFlowLogic = ({ projectTypeStatus }: CustomerProjectTypesStatusesFlowLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const [data, setData] = useState<CustomerProjectTypeModelTypeExt[]>([])
    const [generalSearch, setGeneralSearch] = useState<string>('')

    const getData = useCallback(async () => {
        const result = JSON.parse(JSON.stringify(getAllCustomerProjectTypes() || {}))
        const myData: any = []
        if (result) {
            const parameter = getSingleEmployeeParam('employee_favourite_project')
            let parsedParameter = JSON.parse(parameter || '{}')
            for (const key in result) {
                const item = result[key]
                const customerProjectStatus = item?.CustomerProjectStatusesByType?.[projectTypes[projectTypeStatus] as ProjectTypesInversedKeys]
                if (customerProjectStatus && customerProjectStatus.length) {
                    const idStr = String(item.id)
                    if (parsedParameter[idStr]) {
                        item.checked = true
                        item.order = parseInt(parsedParameter[idStr])
                    } else {
                        item.checked = false
                    }
                    myData.push(item)
                }
            }
            setData(myData.sort((a: TwinDictionary, b: TwinDictionary) => (a.order || Infinity) - (b.order || Infinity)))
        }
    }, [setData, projectTypeStatus])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, generalSearch, setGeneralSearch, openModal, setOpenModal, getData }
}

const modalCustomerProjectTypesStatusesFlowDispatch = {
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
}

const mapCustomerProjectTypesStatusesFlowConnector = (state: AllReduxPayloads) => ({ customerProjectTypes: state.customerProjectTypes })
const modalCustomerProjectTypesStatusesFlowConnect = connect(mapCustomerProjectTypesStatusesFlowConnector, modalCustomerProjectTypesStatusesFlowDispatch)
const modalCustomerProjectTypesStatusesFlowConnectLoading = withLoading(CustomerProjectTypesStatusesFlow, [{ fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }])
const CustomerProjectTypesStatusesFlowConnect = modalCustomerProjectTypesStatusesFlowConnect(modalCustomerProjectTypesStatusesFlowConnectLoading)

export default CustomerProjectTypesStatusesFlowConnect